<template>
  <el-row class="editPanel">
    <h3 :class="{closed: !panelOpen}">Status Workflow</h3><span class="toggle" @click="panelOpen = panelOpen !== true" :class="{closed: !panelOpen}"></span>
    <transition name="panel">
      <div class="pea-panel" v-if="panelOpen">
        <el-form :model="statusForm" ref="statusForm" v-if="this.peaId != 0" :rules="rules">
          <el-row :gutter="20">
            <!-- PEA is denied, withdrawn or other non-editable state -->
            <el-col :lg="4" :md="8" v-if="isApprover === false && canEditFullPea === false && canEditPartialPea === false">
              This PEA cannot be edited in its current state.
            </el-col>
            <!-- PEA being approved and user is owner of PEA-->
            <el-col :lg="4" :md="8" v-if="isApprover === false && canWithdraw && canComplete == false">
              At this stage, those needing to approve this PEA will be notified sequentially based on the authorization level.
              <br>
              Please view the Approvals section for this PEA to see the current Approver and those that are still pending notification.
              <br>
              While this PEA is awaiting approval only the comments can be altered or the PEA can be Withdrawn.
            </el-col>
            <!-- User is approver and has already approved the PEA -->
            <el-col :lg="4" :md="8" v-if="isApprover && canApprovePEA === false">
              You have already entered your approval decision for this PEA but you can still add comments.
            </el-col>
            <el-col :lg="4" :md="8" v-if="canEditFullPea || canEditPartialPea">
              <el-popover
                ref="saveHelp"
                placement="top-start"
                width="350"
                trigger="hover">
                <div v-if="!canComplete">
                  <h4>What this does</h4>
                  <ul>
                    <li>This PEA is saved in the database.</li>
                    <li>This PEA is <strong>NOT</strong> submitted for approval.</li>
                    <li>This PEA is available for viewing by others with sufficient authorization.</li>
                    <li>PEAs that are created but not submitted will be reviewed periodically and may be removed from the system.</li>
                  </ul>
                  <h4>What you can do later</h4>
                  <ul>
                    <li>Once you have all the information required for a PEA return here
                      and select My PEAs/In Preparation or Search to find and edit this PEA again.
                    </li>
                  </ul>
                </div>
                <div v-if="canComplete">
                  <h4>What this does</h4>
                    <ul>
                      <li>Allows others to see how the spending against this PEA is progressing.</li>
                    </ul>
                    <h4>What you should do later</h4>
                    <ul>
                        <li>Keep the expenditure up to date.</li>
                        <li>Mark this PEA as complete when all expenditure has finished.</li>
                    </ul>
                </div>
              </el-popover>
              <el-button type="primary" :loading="this.isLoading" @click="save()" class="buttonCustom" v-popover:saveHelp>Save</el-button><br/>
              <div class="buttonText">
                <div v-if="canRaise || canSubmit">
                  Saving this PEA allows you to partially fill-in the PEA and
                  return later to complete and submit it for approval.
                  <br />
                  Allows you to preview the Approval list.
                </div>
                <div v-if="canComplete">
                  Since this PEA has been approved, only the comments, Cost to Date, and documents can be altered.
                  <br>
                  Save will only update this PEA with any Cost to Date changes.
                </div>
              </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="canEditFullPea && canSubmit">
              <el-popover
                ref="submitHelp"
                placement="top-start"
                width="350"
                trigger="hover">
                <div>
                  <h4>Checklist before submitting</h4>
                  <ul>
                    <li>All expenditure items have been entered.</li>
                    <li>All relevant checks have been performed.</li>
                    <li>You have cleared this PEA with local management and are ready to submit it for approval.</li>
                    </ul>
                  <h4>What you can do later</h4>
                  <ul>
                    <li>If you want to check on the progress of this PEA then return here and select My PEAs/Raised to view the current state.</li>
                  </ul>
                </div>
              </el-popover>
              <el-button type="success" :loading="this.isLoading" @click="submit()" class="buttonCustom" v-popover:submitHelp>Submit</el-button><br/>
                <div class="buttonText">
                  Before submitting this PEA for approval make sure that you have entered
                  all of the assets as they cannot be changed during or after the approval phase.
                </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="canEditFullPea && canRaise">
              Resolution:<br/>
              <el-form-item prop="approvalResolution">
                <el-input type="textarea" v-model="statusForm.approvalResolution" :minlength="3" :maxlength="255" :autosize="{ minRows: 2, maxRows: 5}"></el-input>
              </el-form-item>
              <br />
              <el-button type="success" :loading="this.isLoading" @click="resubmit()" class="buttonCustom" v-popover:submitHelp>Resubmit</el-button><br/>
                <div class="buttonText">
                  Before resubmitting this PEA for approval make sure that you have entered a resolution to the denial condition(s).
                </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="isApprover && canApprovePEA && canApprove">
              Comment:<br/>
              <el-form-item>
                <el-input type="textarea" v-model="statusForm.approveComment" :minlength="3" :maxlength="255" :autosize="{ minRows: 2, maxRows: 5}"></el-input>
              </el-form-item>
              <br />
              <el-popover
                ref="approvePop"
                placement="top"
                width="350"
                v-model="approvePopVisible">
                  Are you sure you want to Approve this PEA?
                  <br>
                  <strong>Note:</strong> Approval decisions are final.
                  <br><br>
                <div style="text-align: right; margin: 0">
                  <el-button size="mini" :loading="this.isLoading" @click="approvePopVisible = false">Cancel</el-button>
                  <el-button type="success" size="mini" :loading="this.isLoading" @click="approve()">Confirm</el-button>
                </div>
              </el-popover>
              <el-button type="success" :loading="this.isLoading" v-popover:approvePop class="buttonCustom">Approve</el-button><br/>
              <div class="buttonText">
                Approve this PEA and move it to the next Approval Level.
                <br> <br>
                If you approve this PEA and it is the last approval required
                then the PEA will be fully approved and spending can be allocated
                against it.
              </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="isApprover && canDeny && canApprovePEA">
              Comment:<br/>
              <el-form-item prop="denyComment">
                <el-input type="textarea" v-model="statusForm.denyComment" :minlength="3" :maxlength="255" :autosize="{ minRows: 2, maxRows: 5}"></el-input>
              </el-form-item>
              <br />
              <el-popover
                ref="denyPop"
                placement="top"
                width="350"
                v-model="denyPopVisible">
                  Are you sure you want to Deny this PEA?
                  <br>
                  <strong>Note:</strong> Approval decisions are final.
                  <br><br>
                <div style="text-align: right; margin: 0">
                  <el-button size="mini" :loading="this.isLoading" @click="denyPopVisible = false">Cancel</el-button>
                  <el-button type="danger" size="mini" :loading="this.isLoading" @click="deny()">Confirm</el-button>
                </div>
              </el-popover>
              <el-button type="danger" :loading="this.isLoading" v-popover:denyPop class="buttonCustom">Deny</el-button><br/>
                <div class="buttonText">
                  Completely Deny this PEA.
                </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="isApprover && canDeny && canApprovePEA">
              Comment:<br/>
              <el-form-item  prop="denyConditionsComment">
                <el-input type="textarea" v-model="statusForm.denyConditionsComment" :minlength="3" :maxlength="255" :autosize="{ minRows: 2, maxRows: 5}"></el-input>
              </el-form-item>
              <br />
              <el-popover
                ref="denyCPop"
                placement="top"
                width="350"
                v-model="denyCPopVisible">
                  Are you sure you want to Deny this PEA with conditions?
                  <br>
                  <strong>Note:</strong> Approval decisions are final.
                  <br><br>
                <div style="text-align: right; margin: 0">
                  <el-button size="mini" :loading="this.isLoading" @click="denyCPopVisible = false">Cancel</el-button>
                  <el-button type="warning" size="mini" :loading="this.isLoading" @click="denyWithConditions()">Confirm</el-button>
                </div>
              </el-popover>
              <el-button type="warning" :loading="this.isLoading" v-popover:denyCPop class="buttonCustom">Deny With Conditions</el-button><br/>
              <div class="buttonText">
                Deny this PEA with Conditions.
                <br><br>
                Allows the submitter of the PEA to meet the submitted conditions and resubmit the PEA for your reapproval before continuing the approval process.
              </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="canUserWithdrawPea && canWithdraw">
              <el-popover
                ref="withdrawHelp"
                placement="top-start"
                width="350"
                trigger="hover">
                <div>
                  <h4>What this does</h4>
                  <ul>
                    <li>If this PEA is pending approval then the currently required approver will be emailed and no further actions will be necesssary.</li>
                    <li>If any approvers have already approved it, those approvers will NOT be emailed. Additionally the total Cost to Date should have been updated and you cannot allocate any further expenditure against this PEA.</li>
                  </ul>
                  <h4>What you can do later</h4>
                  <ul>
                    <li>This PEA will not be deleted and will be available for reference later. You will only be able to update the comments.</li>
                  </ul>
                </div>
              </el-popover>
              <el-button type="danger" :loading="this.isLoading" @click="withdraw()" class="buttonCustom" v-popover:withdrawHelp>Withdraw</el-button><br/>
              <div class="buttonText">
                You can use this option to withdraw this PEA from the approval queue, or if it has already been approved, to indicate that it is not going to be taken any further.
              </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="isApprover && canCancel">
              Comment:<br />
              <el-form-item prop="cancelComment">
                <el-input type="textarea" v-model="statusForm.cancelComment" :minlength="3" :maxlength="255" :autosize="{ minRows: 2, maxRows: 5}"></el-input>
              </el-form-item>
              <br />
              <el-popover
                ref="cancelHelp"
                placement="top-start"
                width="350"
                trigger="hover">
                <div>
                  <h4>What this does</h4>
                  <ul>
                    <li>This PEA will not be deleted but will be be marked as cancelled and will be available for reference later.</li>
                    </ul>
                </div>
              </el-popover>
              <el-button type="danger" :loading="this.isLoading" @click="cancel()" class="buttonCustom" v-popover:cancelHelp>Cancel</el-button><br/>
              <div class="buttonText">
                This can be used to cancel a project or planned expenditure.
              </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="canEditPartialPea && canComplete">
              <el-popover
                ref="completeHelp"
                placement="top-start"
                width="350"
                v-model="completePopVisible">
                <div>
                  <h4>What this does</h4>
                  <ul>
                    <li>Completes this PEA and allows accurate financial reports to be produced comparing planned and actual expenditure.</li>
                    <li>Allows no further spending or updates to this PEA other than to the comments.</li>
                  </ul>
                  Are you sure you wish to complete this PEA?
                  <br><br>
                  <div style="text-align: right; margin: 0">
                    <el-button size="mini" :loading="this.isLoading" @click="completePopVisible = false">Cancel</el-button>
                    <el-button type="success" :loading="this.isLoading" size="mini" @click="complete()">Confirm</el-button>
                  </div>
                </div>
              </el-popover>
              <el-button type="success" :loading="this.isLoading" class="buttonCustom" v-popover:completeHelp>Complete</el-button><br/>
              <div class="buttonText">
                When all of the expenditures against this PEA are complete you should update the final cost and
                then use this option to mark the PEA as completed.
              </div>
            </el-col>
            <el-col :lg="4" :md="8" v-if="canEditFullPea && canDelete">
              <el-popover
                ref="deletePop"
                placement="top"
                width="350"
                v-model="deletePopVisible">
                  Are you sure you want to delete this PEA?
                  <br><br>
                <div style="text-align: right; margin: 0">
                  <el-button size="mini" :loading="this.isLoading" @click="deletePopVisible = false">Cancel</el-button>
                  <el-button type="danger" :loading="this.isLoading" size="mini" @click="deleted()">Confirm</el-button>
                </div>
              </el-popover>
              <el-button type="danger" :loading="this.isLoading" v-popover:deletePop class="buttonCustom">Delete</el-button><br/>
              <div class="buttonText">
                This will completely remove this PEA from the sytem.
              </div>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </transition>
  </el-row>
</template>

<script>
export default {
  name: 'pea-statusworkflow',
  props: {
    peaId: {
      default: 0
    },
    disabled: {
      default: false
    },
    canEditFullPea: {
      default: false
    },
    isApprover: {
      default: false
    },
    canApprovePEA: {
      default: false
    },
    canUserWithdrawPea: {
      default: false
    },
    canEditPartialPea: {
      default: false
    }
  },
  data () {
    var validateDenyComment = (rule, value, callback) => {
      if (value === '' && this.approvalType === 2) {
        callback(new Error('Please enter the reason for the denial'))
      } else {
        callback()
      }
    }
    var validateDenyWithConditionsComment = (rule, value, callback) => {
      if (value === '' && this.approvalType === 3) {
        callback(new Error('Please enter the denial condition that needs to be satisfied'))
      } else {
        callback()
      }
    }
    var validateResubmitComment = (rule, value, callback) => {
      if (value === '' && this.approvalType === 4) {
        callback(new Error('Please enter a comment about the resolution'))
      } else {
        callback()
      }
    }
    var validateCancelComment = (rule, value, callback) => {
      if (value === '' && this.approvalType === 5) {
        callback(new Error('Please enter the reason for the cancellation'))
      } else {
        callback()
      }
    }
    return {
      isLoading: false,
      canSubmit: false,
      canRaise: false,
      canApprove: false,
      canDeny: false,
      canWithdraw: false,
      canCancel: false,
      canDelete: false,
      canComplete: false,
      panelOpen: true,
      approvalType: 0,
      deletePopVisible: false,
      approvePopVisible: false,
      denyPopVisible: false,
      denyCPopVisible: false,
      completePopVisible: false,
      statusForm: {
        approvalResolution: '',
        denyComment: '',
        denyConditionsComment: '',
        approveComment: '',
        cancelComment: ''
      },
      rules: {
        denyConditionsComment: [
          { validator: validateDenyWithConditionsComment, required: true, trigger: 'blur' }
        ],
        denyComment: [
          { validator: validateDenyComment, required: true, trigger: 'blur' }
        ],
        cancelComment: [
          { validator: validateCancelComment, required: true, trigger: 'blur' }
        ],
        approvalResolution: [
          { validator: validateResubmitComment, required: true, trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    startup () {
      this.fetchData()
    },
    fetchData: function () {
      var call = '/workflow/next/' + this.peaId + '/all'
      this.$axios.get(call).then(response => {
        this.canSubmit = response.data.Submitted
        this.canRaise = response.data.Raised
        this.canApprove = response.data.Approved
        this.canDeny = response.data.Denied
        this.canWithdraw = response.data.Withdrawn
        this.canCancel = response.data.Cancelled
        this.canDelete = response.data.Deleted
        this.canComplete = response.data.Complete
      })
    },
    save () {
      this.updateLoading(true)
      this.$emit('save')
    },
    submit () {
      this.updateLoading(true)
      this.$emit('updateWorkflow', {newstatus: 'submit', comment: ''})
    },
    resubmit () {
      this.updateLoading(true)
      this.approvalType = 4
      this.$refs.statusForm.validate((valid) => {
        if (valid) {
          this.$emit('updateWorkflow', { newstatus: 'resubmit', comment: this.statusForm.approvalResolution })
        } else {
          this.updateLoading(false)
        }
      })
    },
    approve () {
      this.updateLoading(true)
      this.approvalType = 1
      this.approvePopVisible = false
      this.$emit('updateWorkflow', {newstatus: 'approve', comment: this.statusForm.approveComment})
    },
    deny () {
      this.updateLoading(true)
      this.approvalType = 2
      this.denyPopVisible = false
      this.$refs.statusForm.validate((valid) => {
        if (valid) {
          this.$emit('updateWorkflow', { newstatus: 'deny', comment: this.statusForm.denyComment })
        } else {
          this.updateLoading(false)
        }
      })
    },
    denyWithConditions () {
      this.updateLoading(true)
      this.approvalType = 3
      this.denyCPopVisible = false
      this.$refs.statusForm.validate((valid) => {
        if (valid) {
          this.$emit('updateWorkflow', { newstatus: 'denyWithConditions', comment: this.statusForm.denyConditionsComment })
        } else {
          this.updateLoading(false)
        }
      })
    },
    withdraw () {
      this.updateLoading(true)
      this.$emit('updateWorkflow', {newstatus: 'withdraw', comment: ''})
    },
    cancel () {
      this.updateLoading(true)
      this.approvalType = 5
      this.$refs.statusForm.validate((valid) => {
        if (valid) {
          this.$emit('updateWorkflow', { newstatus: 'cancel', comment: this.statusForm.cancelComment })
        } else {
          this.updateLoading(false)
        }
      })
    },
    complete () {
      this.updateLoading(true)
      this.completePopVisible = false
      this.$emit('updateWorkflow', {newstatus: 'complete', comment: ''})
    },
    deleted () {
      this.updateLoading(true)
      this.deletePopVisible = false
      this.$emit('updateWorkflow', { newstatus: 'delete', comment: '' })
    },
    updateLoading (to) {
      this.isLoading = to
    }
  },
  mounted () {
    this.startup()
  },
  watch: {
    // watch for route change - going from pea/4 to pea/0
    '$route': 'startUp'
  }
}
</script>

<style scoped>
.helpIcon {
  width: 27px;
  height: 27px;
}
.buttonCustom {
  width: 100%;
}
.buttonText {
  text-align: center;
}
</style>
